import { calculateDistanceToFacilityInMiles } from "@src/appV2/Facilities/utils/index";
import type { Worker } from "@src/appV2/Worker/api/types";
import { Agent, Shift } from "@src/lib/interface";
import moment from "moment-timezone";

import { simplePlural } from "./strings";
import { MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS } from "../hcpShifts/constants";

const doesShiftIncludeLunchBreak = (
  start: string | undefined,
  end: string | undefined,
  duration?: number
) => {
  if (duration && duration > 0) {
    return duration > MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS;
  }
  if (!start || !end) {
    return true;
  }
  const nonLunchDuration = moment.duration(moment(end).diff(moment(start))).asHours();
  return nonLunchDuration > MINIMUM_TIME_FOR_MANDATORY_BREAK_IN_HOURS;
};

export const requiredLunchBreakDuration = (
  shift: Shift,
  mandatoryBreakDurationInMinutes: number
) => {
  let requiresLunchBreak = shift.facility?.verificationPreference?.requiresLunchBreak ?? false;
  let lunchBreakDuration = "";

  if (doesShiftIncludeLunchBreak(shift.start, shift.end)) {
    lunchBreakDuration = `${mandatoryBreakDurationInMinutes} minute`;
  } else {
    requiresLunchBreak = false;
  }
  return [requiresLunchBreak, lunchBreakDuration];
};

export const formatShiftDurationByDifference = (
  start: string,
  end: string,
  facilityTimezone: string,
  mandatoryBreakDurationInMinutes: number
): string => {
  const hoursString = formatShitStartAndEnd(start, end, facilityTimezone);

  const shiftStart = moment(start).tz(facilityTimezone);
  const shiftEnd = moment(end).tz(facilityTimezone);
  const nonLunchDuration = moment.duration(shiftEnd.diff(shiftStart)).asHours();
  const durationInHours = doesShiftIncludeLunchBreak(start, end, nonLunchDuration)
    ? nonLunchDuration - mandatoryBreakDurationInMinutes / 60
    : nonLunchDuration;
  const hours = Math.trunc(durationInHours);
  const minutes = Math.trunc((durationInHours - hours) * 60);
  const durationString = `${hours} ${simplePlural("hr", hours)}${
    minutes ? ` ${minutes} ${simplePlural("min", minutes)}` : ""
  }`;

  return `${hoursString} (${durationString})`;
};

const formatShitStartAndEnd = (start: string, end: string, facilityTimezone: string) => {
  const shiftStart = moment(start).tz(facilityTimezone);
  const shiftEnd = moment(end).tz(facilityTimezone);
  return `${shiftStart.format("hh:mm A")} - ${shiftEnd.format("hh:mm A")}`;
};

export function getShiftFacilityDistanceText(
  shift: Shift,
  agent: Agent | Worker,
  isUrgentShift: boolean
): string {
  const distance = calculateDistanceToFacilityInMiles(agent, shift.facility);

  if (distance === -1) {
    return "";
  }

  const distFrom = isUrgentShift ? "away from you" : "from home";

  if (distance < 1) {
    return `~${distance.toFixed(1)} miles ${distFrom}`;
  }
  if (distance === 1) {
    return `~${distance} mile ${distFrom}`;
  }
  return `~${distance.toFixed(1)} miles ${distFrom}`;
}
